import { useSelector } from 'react-redux';

import clsx from 'clsx';
import { SelectProps } from 'antd/lib/select';

import { CoreSelectProps } from '../types/props';

import { usePreselect } from './use-preselect';
import { useLoadMore } from './use-load-more';
import { useActive } from './use-active';
import { useOptions } from './use-options';
import { useDefault } from './use-default';
import { selectOnLeaveHandler } from './select-on-leave-handler';

import { Nullable } from '@common/typescript/objects/Nullable';
import { getLoadableScrollHandler } from '@common/react/components/Utils/Utils';

import { SelectList, getSelectItemState } from '@app/store/SelectList/SelectList';
import { SelectRequestParams } from '@app/store/SelectList/ListActions';
import { StoreType } from '@app/store/SelectList/UtilityTypes';
import { ApplicationState } from '@app/store';
import { useDebounce } from '@app/hooks/useDebounce';

export function useSelect<
	TStore extends keyof SelectList,
	TParams extends SelectRequestParams<number>
>(props: CoreSelectProps<number, TStore, TParams>): SelectProps<number> | SelectProps<Array<number>> {
	const root: StoreType<TStore> = useSelector<ApplicationState, StoreType<TStore>>((state: ApplicationState) =>
		state.selects[props.store]);
	const store = getSelectItemState(root);

	const debounce = useDebounce<string>((value: Nullable<string>) => {
		const str = value ?? '';
		props.onSearch?.(str);
	}, 750);

	usePreselect(props);
	useDefault(props);

	const loadMore = useLoadMore(props);
	const active = useActive(props);
	const options = useOptions(props);

	const properties = {
		showSearch: true,
		showArrow: true,
		allowClear: props.allowClear ?? true,
		showAction: ['focus', 'click'],

		value: active.isEmpty ? undefined : props.value,
		onChange: props.onChange,

		size: props.size,
		bordered: props.bordered,
		placement: props.placement,
		dropdownMatchSelectWidth: props.dropdownMatchSelectWidth,

		onSearch: (query: string) => debounce(query),
		onSelect: (value) => {
			props.onSearch?.('');
			if (value === undefined) return;

			props.onSelect?.(value);
		},
		onDeselect: (value) => {
			if (value === undefined) return;

			props.onDeselect?.(value);
		},
		onPopupScroll: getLoadableScrollHandler(loadMore.fetch, loadMore.shouldFetch),
		onFocus: () => {
			active.setFocus(true);
			props.onFocus?.();
		},
		onBlur: () => {
			active.setFocus(false);

			props.onSearch?.('');
			props.onBlur?.();
		},
		onInputKeyDown: selectOnLeaveHandler,

		optionFilterProp: props.filterOption ? 'children' : undefined,
		filterOption: props.filterOption ?? false,
		mode: props.multiple ? 'multiple' : undefined,

		className: clsx(props.selectClassName, active.isActive && props.activeClassName),
		placeholder: props.placeholder,
		loading: store.isLoading,
		disabled: props.disabled,
		options,
	};

	if (props.multiple) return properties as SelectProps<Array<number>>;

	return properties as SelectProps<number>;
}
